import i18n from 'i18next'
import {
  closeSessionAndPort,
  createSerialSession,
  OnSerialOutput,
  OnTerminalLog,
  Serial,
  SerialNavigator,
  SerialPortConnection,
} from './serial'

const USB_RECONNECT_POLL_MS = 1000
const USB_REENUMERATION_TIMEOUT_MS = 60000

type UsbConnectionConfig = {
  vendorId: number
  productId: number
  baudRate: number
}

const throwIfAborted = (abortSignal: AbortSignal): void => {
  if (abortSignal.aborted) {
    throw new Error('upload aborted')
  }
}

const sleepWithAbort = async (
  ms: number,
  abortSignal: AbortSignal,
): Promise<void> => {
  throwIfAborted(abortSignal)

  await new Promise<void>((resolve, reject) => {
    const timeout = setTimeout(() => {
      abortSignal.removeEventListener('abort', onAbort)
      resolve()
    }, ms)

    const onAbort = () => {
      clearTimeout(timeout)
      abortSignal.removeEventListener('abort', onAbort)
      reject(new Error('upload aborted'))
    }

    abortSignal.addEventListener('abort', onAbort)
  })
}

const waitForDisconnect = async (
  isNmdiPort: (serialPort: SerialPortConnection) => boolean,
  onTerminalLog: OnTerminalLog,
  abortSignal: AbortSignal,
): Promise<void> => {
  const disconnectDeadline = Date.now() + USB_REENUMERATION_TIMEOUT_MS
  while (Date.now() < disconnectDeadline) {
    throwIfAborted(abortSignal)
    onTerminalLog(
      i18n.t(
        'device.calibration.preFlightStep.certificateUploadFlow.logs.waitingForDisconnect',
      ),
    )
    const serialNavigator = navigator as SerialNavigator
    const ports = await serialNavigator.serial?.getPorts()
    const nmdiFound = ports?.some(isNmdiPort)
    if (!nmdiFound) {
      onTerminalLog(
        i18n.t(
          'device.calibration.preFlightStep.certificateUploadFlow.logs.disconnected',
        ),
      )
      return
    }
    await sleepWithAbort(USB_RECONNECT_POLL_MS, abortSignal)
  }

  throw new Error(
    i18n.t(
      'device.calibration.preFlightStep.certificateUploadFlow.errors.disconnectTimeout',
    ),
  )
}

const waitForReconnect = async (
  isNmdiPort: (serialPort: SerialPortConnection) => boolean,
  onTerminalLog: OnTerminalLog,
  abortSignal: AbortSignal,
): Promise<SerialPortConnection> => {
  const reconnectDeadline = Date.now() + USB_REENUMERATION_TIMEOUT_MS
  while (Date.now() < reconnectDeadline) {
    throwIfAborted(abortSignal)
    onTerminalLog(
      i18n.t(
        'device.calibration.preFlightStep.certificateUploadFlow.logs.waitingForReconnect',
      ),
    )
    const serialNavigator = navigator as SerialNavigator
    const ports = await serialNavigator.serial?.getPorts()
    const reconnectedPort = ports?.find(isNmdiPort)
    if (reconnectedPort) {
      onTerminalLog(
        i18n.t(
          'device.calibration.preFlightStep.certificateUploadFlow.logs.reconnected',
        ),
      )
      return reconnectedPort
    }

    await sleepWithAbort(USB_RECONNECT_POLL_MS, abortSignal)
  }

  throw new Error(
    i18n.t(
      'device.calibration.preFlightStep.certificateUploadFlow.errors.disconnectReconnectTimeoutAfterSwitch',
    ),
  )
}

export const reconnect = async (
  session: Serial,
  activePort: SerialPortConnection,
  { baudRate, productId, vendorId }: UsbConnectionConfig,
  onTerminalLog: OnTerminalLog,
  onSerialOutput: OnSerialOutput,
  abortSignal: AbortSignal,
): Promise<{ session: Serial; activePort: SerialPortConnection }> => {
  throwIfAborted(abortSignal)
  onTerminalLog(
    i18n.t(
      'device.calibration.preFlightStep.certificateUploadFlow.logs.closingShellSessionBeforeReenumeration',
    ),
  )
  await closeSessionAndPort(session, activePort)

  const isNmdiPort = (serialPort: SerialPortConnection): boolean => {
    const portInfo = serialPort.getInfo?.()
    return (
      portInfo?.usbVendorId === vendorId && portInfo?.usbProductId === productId
    )
  }

  await waitForDisconnect(isNmdiPort, onTerminalLog, abortSignal)
  activePort = await waitForReconnect(isNmdiPort, onTerminalLog, abortSignal)
  throwIfAborted(abortSignal)
  onTerminalLog(
    i18n.t(
      'device.calibration.preFlightStep.certificateUploadFlow.logs.openingReconnectedPort',
    ),
  )
  await activePort.open({ baudRate })
  return {
    session: createSerialSession({
      activePort,
      onTerminalLog,
      onSerialOutput,
    }),
    activePort,
  }
}
